import { FieldInfo } from '@/components/FieldInfo';
import { useRedirectStore } from '@/stores/redirect';
import NautLogo from '@nautilusbio/nb-design-system/assets/naut-logo-sm.png';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { useForm } from '@tanstack/react-form';
import { useSearch } from '@tanstack/react-router';
import { Loader } from 'lucide-react';
import React, { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import createStyles from '../../components/Create/styles.module.css';
import { server } from '../../constants.ts';
import { useForgotPassword } from '../ResetPassword/api/useForgotPassword.ts';

import styles from './styles.module.css';

const loginUrl = `${server}/auth/login${import.meta.env.DEV ? `?redirect_uri=${import.meta.env.VITE_WEB_SERVER}` : ''}`;

const Login: FC = () => {
  const { t } = useTranslation('login');
  const { handleForgotPassword } = useForgotPassword();
  const search = useSearch({ from: '/login' });
  const initialEmail = search?.email || '';
  const initialMode = search?.mode === 'password' ? 'password' : 'sso';
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [mode, setMode] = useState<'sso' | 'password'>(initialMode);

  // Zustand store for redirect path
  const setRedirectPath = useRedirectStore(useShallow(state => state.setRedirectPath));

  // Store redirect path on mount if present in query
  useEffect(() => {
    if (search?.redirect) {
      setRedirectPath(search.redirect);
    }
  }, [search?.redirect, setRedirectPath]);

  const userSchema = useMemo(
    () =>
      z.object({
        email: z.email(t('form.labels.invalid_email')).min(1, t('form.labels.email_required')),
        password: z
          .string()
          .min(mode === 'password' ? 1 : 0, mode === 'password' ? t('form.labels.pass_required') : ''),
      }),
    [mode, t],
  );

  const form = useForm({
    defaultValues: {
      email: initialEmail,
      password: '',
    },
    validators: {
      onChange: userSchema,
    },
  });

  useEffect(() => {
    if (initialEmail) {
      form.setFieldValue('email', initialEmail);
    }
    if (initialMode === 'password') {
      setMode('password');
    }
  }, [form, initialEmail, initialMode]);

  const logoAlt = t('alt', {
    ns: 'logo',
  });

  return (
    <form action={loginUrl} className={styles.form} id="login_form" method="POST">
      <img alt={logoAlt} className={styles.logo} src={NautLogo} title={logoAlt} />

      <Stack alignItems="center" fullWidth gap={4} orientation="vertical">
        <form.Field name="email">
          {field => (
            <Stack className={styles.inputContainer} fullWidth orientation="vertical">
              <Input
                className={styles.email}
                data-testid="input-email"
                fullWidth
                id={field.name}
                label={{ children: t('form.labels.email'), htmlFor: field.name, id: 'label-email' }}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={e => field.handleChange(e.target.value)}
              />
              <FieldInfo field={field} />
            </Stack>
          )}
        </form.Field>

        {mode === 'password' && (
          <form.Field name="password">
            {field => (
              <Stack className={styles.inputContainer} fullWidth orientation="vertical">
                <Input
                  /* eslint-disable-next-line jsx-a11y/no-autofocus */
                  autoFocus
                  className={styles.email}
                  data-testid="input-pass"
                  fullWidth
                  id={field.name}
                  label={{ children: t('form.labels.pass'), htmlFor: field.name, id: 'label-pass' }}
                  name={field.name}
                  type="password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.value)}
                />
                <FieldInfo field={field} />
              </Stack>
            )}
          </form.Field>
        )}

        <Stack alignItems="center" fullWidth gap={1} orientation="horizontal">
          <Button
            aria-label={mode === 'password' ? t('buttons.login') : t('buttons.use_password')}
            data-testid={mode === 'password' ? 'sso-mode-btn' : 'show-password-btn'}
            fullWidth
            onPress={() => setMode(mode === 'password' ? 'sso' : 'password')}
            visual="unstyled"
          >
            {mode === 'password' ? t('buttons.loginsso') : t('buttons.use_password')}
          </Button>

          {mode === 'password' && (
            <form.Subscribe selector={state => [state.values.email, state.fieldMeta.email?.errors]}>
              {([email, emailErrors]) => {
                const emailValue = typeof email === 'string' ? email.trim() : '';
                const isEmailValid = !emailErrors?.length && !!emailValue;

                return (
                  <Tooltip
                    content={t('form.labels.valid_email_required')}
                    isDisabled={isEmailValid}
                    placement="right"
                    trigger={
                      <Button
                        className={styles.forgotPasswordLink}
                        data-testid="forgot-password-link"
                        fullWidth
                        isDisabled={!isEmailValid}
                        onPress={() => handleForgotPassword(String(email))}
                        visual="unstyled"
                      >
                        {t('form.labels.forgot_password')}
                      </Button>
                    }
                  />
                );
              }}
            </form.Subscribe>
          )}
        </Stack>
      </Stack>

      <Stack className={styles.footer} gap={4}>
        <form.Subscribe selector={state => [state.values.email, state.values.password, state.isSubmitting]}>
          {([email, password, isSubmitting]) => {
            const emailValue = typeof email === 'string' ? email.trim() : '';
            const isEmailValid = !form.state.fieldMeta.email?.errors?.length && !!emailValue;
            const isPasswordValid = mode !== 'password' || (typeof password === 'string' && password.length >= 1);
            const canLogin = isEmailValid && isPasswordValid;

            return (
              <Button
                data-testid="submit-btn"
                fullWidth
                isDisabled={!canLogin}
                onPress={() => setIsSubmitted(true)}
                size="xl"
                type="submit"
                visual={isSubmitted ? 'success' : 'primary'}
              >
                {isSubmitting || isSubmitted ? (
                  <>
                    <Loader className={createStyles.spinner} /> &nbsp;{t('logging_in', { ns: 'general' })}
                  </>
                ) : (
                  t('buttons.login')
                )}
              </Button>
            );
          }}
        </form.Subscribe>
      </Stack>
    </form>
  );
};

export default Login;
